import { Button } from "@/components/ui/button";
import type { ListBox, Read } from "@/components/ui/box.schema";
import type { Announcement, AnnouncementFeed } from "@/lib/api/announcements";
import { inboxWords as words, messageTime } from "./inbox-copy";

export interface NoticeReadMark { failed: boolean; pending: boolean; retry(): void }
/**
 * The notice history. `pinned` is a course's own All notices: the band names
 * the course, so rows and facets never repeat it and only the scope that
 * differs (Academy-wide) is row text.
 */
export function noticesBox({read,course,pinned=false,onCourseChange,selected,mark,query,onQueryChange}:{
  read:Read<AnnouncementFeed>;course:string;pinned?:boolean;onCourseChange(course:string):void;selected?:string;
  mark(id:string):NoticeReadMark;query:string;onQueryChange(query:string):void;
}):ListBox<AnnouncementFeed,Announcement> {
  const value=read.status==="ready"?read.value:read.status==="stale"?read.last:null;
  const courses=new Map(value?.items.filter(item=>item.course_id).map(item=>[item.course_id!,item.course_title??words.course]));
  if(course && !courses.has(course))courses.set(course,words.selectedCourse);
  const shown=(item:Announcement)=>(!course||item.course_id===course) && (!query.trim()||item.title.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
  return {
    id:"notices",kind:"list",label:pinned?words.allNotices:words.notices,job:words.readNotices,owner:{system:"imam"},fold:"never",
    read:read.status==="ready"&&!read.value.items.length?{status:"empty",sentence:words.noNotices}:read,
    // The figure owns the count and rows mark which are unread; the head says only whether any are.
    state:feed=>feed.items.some(item=>!item.is_read)?{class:"waiting",word:words.unreadNotices}:{class:"done",word:words.allRead},
    detail:()=>pinned?words.courseAndInstitution:words.institutionAndCourses,
    figures:feed=>[{value:feed.items.length,unit:feed.items.length===1?words.notice:words.noticesUnit}],
    rows:feed=>feed.items.filter(shown),
    find:value?{placeholder:words.findNotice,onQueryChange,facets:pinned?[]:[{label:words.course,value:course,
      options:[{value:"",label:words.allCourses},...[...courses].map(([value,label])=>({value,label}))],onChange:onCourseChange}],
      hidden:value.items.filter(item=>!shown(item)).length}:undefined,
    row:{key:item=>item.id,title:item=>item.title,
      state:item=>item.is_urgent?{class:"waiting",word:item.is_read?words.urgent:words.urgentUnread}:!item.is_read?{class:"reading",word:words.unread}:null,
      // An urgent notice says Urgent once, in its state, never again as its category (#1416).
      facts:item=>[item.is_urgent&&item.category==="urgent"?null:words.categories[item.category],item.scope==="academy"?words.academyWide:pinned?null:item.course_title??words.course,messageTime(item.published_at)].filter(Boolean).join(" · "),
      act:()=>({does:"open",verb:words.open}),startsOpen:item=>item.id===selected,
      detail:item=>{
        const status=mark(item.id);
        return <div className="grid gap-3 py-3">
          <p className="whitespace-pre-wrap break-words text-body">{item.body}</p>
          {status.failed && <div className="grid gap-2" role="status">
            <p className="text-ui text-muted-foreground">{words.markReadFailed}</p>
            <Button variant="ghost" disabled={status.pending} onClick={status.retry}>{words.tryMarkRead}</Button>
          </div>}
        </div>;
      },
    },
  };
}
